import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { activeScopeKey, useActiveScope } from '@features/account-view/active';
import CatalogService from '@utility/services/catalog';
import { divisionCountries, foldMarkets } from '../lib/markets-api';
import { catalogKeys } from '../lib/query-keys';

const NO_DIVISIONS: readonly string[] = [];

/* The facet changes only when listings sync; the picker need not ask on every visit. */
const FIVE_MINUTES = 5 * 60_000;

/**
 * M2: the countries the current scope and division filter list in, counted for the list's own query `listParams`
 * (items per country, ids with no country as NO_COUNTRY), and at the account level the divisions that sell in one country only, for their flag. `available`
 * stays false while the facet loads or when the API has none (404 before the server ships): the picker hides then.
 */
export function useCatalogMarkets(listParams: object = {}) {
  const { level, divisionFilter } = useActiveScope();
  const scope = activeScopeKey();
  const filtered = useQuery({
    queryKey: catalogKeys.markets(scope, listParams),
    queryFn: () => CatalogService.markets(listParams),
    select: data => foldMarkets(data.markets),
    /* A new view or search asks again; the picker stays, with the last counts, until the answer comes. */
    placeholderData: keepPreviousData,
    staleTime: FIVE_MINUTES,
    retry: false,
  });
  /* The division labels read every division, whichever are picked, and are the same in every view. */
  const everyDivision = useQuery({
    queryKey: catalogKeys.markets({ level, divisionFilter: NO_DIVISIONS }),
    queryFn: () => CatalogService.markets({}, true),
    select: data => divisionCountries(data.divisions),
    staleTime: FIVE_MINUTES,
    retry: false,
    enabled: level === 'account',
  });

  return {
    available: filtered.isSuccess,
    countries: filtered.data ?? [],
    divisionCountries: everyDivision.data,
    /** The division filter narrows the list (account level). */
    divisionsFiltered: level === 'account' && divisionFilter.length > 0,
  };
}
